import { create } from 'zustand'

export interface Event {
  id: string
  title: string
  description: string
  date: string
  time: string
  location: string
  image: string
  category: string
  price: number
  capacity: number
  registeredCount: number
}

interface EventState {
  events: Event[]
  selectedEvent: Event | null
  isLoading: boolean
  fetchEvents: () => Promise<void>
  fetchEventById: (id: string) => Promise<void>
  createEvent: (event: Omit<Event, 'id'>) => Promise<void>
  updateEvent: (id: string, event: Partial<Event>) => Promise<void>
  deleteEvent: (id: string) => Promise<void>
}

const mockEvents: Event[] = [
  {
    id: '1',
    title: 'Elegant Garden Wedding Expo 2024',
    description: 'Discover the most beautiful garden wedding venues, floral arrangements, and outdoor ceremony ideas. Perfect for couples dreaming of a romantic outdoor celebration.',
    date: '2024-03-15',
    time: '10:00 AM',
    location: 'Botanical Gardens, Manila',
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800',
    category: 'Garden Wedding',
    price: 1500,
    capacity: 200,
    registeredCount: 120
  },
  {
    id: '2',
    title: 'Beach Wedding Paradise Showcase',
    description: 'Experience the magic of beach weddings with stunning coastal venues, tropical decorations, and sunset ceremony inspiration.',
    date: '2024-03-20',
    time: '02:00 PM',
    location: 'Boracay Convention Center',
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800',
    category: 'Beach Wedding',
    price: 2000,
    capacity: 150,
    registeredCount: 95
  },
  {
    id: '3',
    title: 'Traditional Filipino Wedding Festival',
    description: 'Celebrate Filipino culture with traditional wedding customs, native attire, and authentic Filipino cuisine for your special day.',
    date: '2024-03-25',
    time: '11:00 AM',
    location: 'Cultural Center of the Philippines',
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800',
    category: 'Traditional Wedding',
    price: 1200,
    capacity: 300,
    registeredCount: 250
  },
  {
    id: '4',
    title: 'Luxury Wedding & Reception Planning',
    description: 'Plan your dream luxury wedding with top-tier vendors, premium venues, and exclusive wedding services for the perfect celebration.',
    date: '2024-04-01',
    time: '01:00 PM',
    location: 'Makati Shangri-La Hotel',
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800',
    category: 'Luxury Wedding',
    price: 3000,
    capacity: 100,
    registeredCount: 75
  }
]

export const useEventStore = create<EventState>((set, get) => ({
  events: [],
  selectedEvent: null,
  isLoading: false,
  
  fetchEvents: async () => {
    set({ isLoading: true })
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800))
      set({ events: mockEvents, isLoading: false })
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },
  
  fetchEventById: async (id: string) => {
    set({ isLoading: true })
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500))
      const event = mockEvents.find(e => e.id === id)
      set({ selectedEvent: event || null, isLoading: false })
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },
  
  createEvent: async (event: Omit<Event, 'id'>) => {
    set({ isLoading: true })
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      const newEvent: Event = { ...event, id: Date.now().toString() }
      set(state => ({ 
        events: [...state.events, newEvent], 
        isLoading: false 
      }))
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },
  
  updateEvent: async (id: string, event: Partial<Event>) => {
    set({ isLoading: true })
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800))
      set(state => ({
        events: state.events.map(e => e.id === id ? { ...e, ...event } : e),
        selectedEvent: state.selectedEvent?.id === id ? { ...state.selectedEvent, ...event } : state.selectedEvent,
        isLoading: false
      }))
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },
  
  deleteEvent: async (id: string) => {
    set({ isLoading: true })
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500))
      set(state => ({
        events: state.events.filter(e => e.id !== id),
        selectedEvent: state.selectedEvent?.id === id ? null : state.selectedEvent,
        isLoading: false
      }))
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  }
}))
